<script setup lang="ts">
import { toDisplayDate } from '../utils/displayDate'
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '../stores/notifications'
import type { AppNotification } from '../services/notificationApi'

const store = useNotificationStore()
const router = useRouter()
const op = ref()

function onSwMessage(event: MessageEvent) {
  if (event.data?.type === 'push-notification') {
    store.fetchUnreadCount()
    store.fetchItems()
  }
}

onMounted(() => {
  store.startPolling()
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', onSwMessage)
  }
})

onUnmounted(() => {
  store.stopPolling()
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', onSwMessage)
  }
})

function toggle(e: Event) {
  // Toggle synchronously — PrimeVue anchors the popover to e.currentTarget,
  // which is null once the handler yields (any await). Items refresh in the
  // background and update reactively while the popover is open.
  op.value.toggle(e)
  void store.fetchItems()
}
function toggleUnreadOnly() {
  store.unreadOnly = !store.unreadOnly
  void store.fetchItems()
}
async function openOne(n: AppNotification) {
  await store.markRead(n.id)
  op.value.hide()
  if (n.link) router.push(n.link)
}
</script>

<template>
  <PButton icon="pi pi-bell" text v-tooltip="'Notifications'"
    :badge="store.unreadCount > 0 ? String(store.unreadCount) : undefined" badgeSeverity="danger"
    @click="toggle" aria-label="Notifications" />
  <PPopover ref="op">
    <div class="w-80">
      <div class="flex items-center justify-between border-b px-3 py-2">
        <span class="font-medium">Notifications</span>
        <!-- Marks only the notifications listed below (ops audit shell #2). -->
        <PButton label="Mark these read" text size="small" @click="store.markAllRead()" />
      </div>
      <div class="flex items-center justify-between border-b px-3 py-1.5 text-xs text-secondary-500">
        <label class="flex cursor-pointer items-center gap-1.5">
          <input type="checkbox" :checked="store.unreadOnly" @change="toggleUnreadOnly" />
          Unread only
        </label>
        <span v-if="store.total > store.items.length">Newest {{ store.items.length }} of {{ store.total }}</span>
      </div>
      <div v-if="store.itemsError" role="alert" class="flex items-center justify-between px-3 py-4 text-sm text-red-700">
        Couldn't load notifications.
        <PButton label="Retry" text size="small" @click="store.fetchItems()" />
      </div>
      <div v-else-if="store.items.length === 0" class="px-3 py-6 text-center text-sm text-secondary-500">
        {{ store.unreadOnly ? 'Nothing unread.' : 'Nothing yet.' }}
      </div>
      <button v-for="n in store.items" :key="n.id"
        class="block w-full border-b px-3 py-2 text-left hover:bg-secondary-50"
        :class="{ 'bg-indigo-50': !n.read_at }" @click="openOne(n)">
        <div class="text-sm font-medium">{{ n.title }}</div>
        <div v-if="n.body" class="line-clamp-2 text-xs text-secondary-600">{{ n.body }}</div>
        <div class="text-[10px] text-secondary-400">{{ toDisplayDate(n.created_at).toLocaleString() }}</div>
      </button>
    </div>
  </PPopover>
</template>
